import * as React from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { brand, text, surface } from "../../theme";


function useCountUp(value, duration = 600) {
  const [display, setDisplay] = React.useState(value);
  const isNumeric =
    typeof value === "number" || /^[\d,.\s₹$€£%]+$/.test(String(value));

  React.useEffect(() => {
    if (!isNumeric) {
      setDisplay(value);
      return;
    }
    const numeric = parseFloat(String(value).replace(/[^0-9.]/g, "")) || 0;
    const prefix = String(value).match(/^[^\d]*/)?.[0] || "";
    const suffix = String(value).match(/[^\d.]*$/)?.[0] || "";
    let start = null;

    const animate = (timestamp) => {
      if (!start) start = timestamp;
      const progress = Math.min((timestamp - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = Math.round(numeric * eased);
      setDisplay(`${prefix}${current.toLocaleString("en-IN")}${suffix}`);
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [value, duration, isNumeric]);

  return display;
}

function StatCard({
  title,
  value,
  icon,
  iconBg = brand[50],
  iconColor = brand.main,
  onClick,
}) {
  const animatedValue = useCountUp(value);

  const renderedIcon = React.isValidElement(icon)
    ? React.cloneElement(icon, { size: 22, strokeWidth: 2 })
    : icon;

  // Soft pastel background derived from the icon color
  const softBg = `color-mix(in srgb, ${iconColor} 8%, ${surface.cardBg})`;
  const softBorder = `color-mix(in srgb, ${iconColor} 18%, ${surface.cardBg})`;

  return (
    <Card
      elevation={0}
      onClick={onClick}
      sx={{
        position: "relative",
        overflow: "hidden",
        height: 135,
        borderRadius: "14px",
        border: `1px solid ${softBorder}`,
        background: softBg,
        boxShadow: "0 1px 2px rgba(16,24,40,.03)",
        transition: "all .25s ease",
        p: 2,
        cursor: onClick ? "pointer" : "default",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        "&:hover": {
          transform: "translateY(-3px)",
          borderColor: `${iconColor}55`,
          boxShadow: `0 12px 24px -8px ${iconColor}30`,
        },
        "&:hover .stat-arrow": {
          transform: "translateX(4px)",
        },
      }}
    >
      {/* Top row: Icon (left) + Title & Value (right) */}
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <Box
          sx={{
            width: 46,
            height: 46,
            borderRadius: "12px",
            background: iconColor,
            color: text.inverse,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            boxShadow: `0 4px 10px ${iconColor}45`,
          }}
        >
          {renderedIcon}
        </Box>

        <Box sx={{ flex: 1, minWidth: 0, pt: 0.25 }}>
          <Typography
            noWrap
            sx={{
              fontSize: ".78rem",
              fontWeight: 600,
              color: text.secondary,
              letterSpacing: ".01em",
              mb: 0.25,
            }}
          >
            {title}
          </Typography>
          <Typography
            noWrap
            sx={{
              fontSize: "1.65rem",
              fontWeight: 800,
              color: text.primary,
              lineHeight: 1.1,
              letterSpacing: "-0.5px",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {animatedValue}
          </Typography>
        </Box>
      </Stack>

      {/* Bottom View Details link */}
      <Stack
        direction="row"
        alignItems="center"
        spacing={0.5}
        sx={{
          color: iconColor,
          fontSize: ".72rem",
          fontWeight: 600,
          pt: 1,
          borderTop: `1px dashed ${iconColor}30`,
        }}
      >
        <Typography sx={{ fontSize: ".72rem", fontWeight: 600, color: text.secondary }}>
          View Details
        </Typography>
        <ArrowForwardIcon
          className="stat-arrow"
          sx={{
            fontSize: 14,
            color: iconColor,
            transition: "transform .25s ease",
          }}
        />
      </Stack>
    </Card>
  );
}

StatCard.propTypes = {
  title: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  icon: PropTypes.node,
  iconBg: PropTypes.string,
  iconColor: PropTypes.string,
  onClick: PropTypes.func,
};

export default StatCard;